import React from 'react';
import CtySpinner from '../spinner/CtySpinner';
import { ErrorBoundaryCatch } from './ErrorBoundaryCatch';

// HOC definition
interface WithLoadingAndErrorProps {
    isLoading?: boolean;
    error?: any;
    spinnerSize?: 'sm' | 'md' | 'lg';
    [key: string]: any;
}

const catchExceptionsForComponent = <P extends object>(
    Component: React.ComponentType<P>
): React.ComponentType<P> => {
    const WrappedComponent = React.forwardRef<any, P>((props, ref) => {
        return (
            <ErrorBoundaryCatch>
                <Component {...props} ref={ref} />
            </ErrorBoundaryCatch>
        );
    });

    // Set display name for better debugging
    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

    return WrappedComponent as React.ComponentType<P>;
}

const ErrorBoundary = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
    // Create the wrapped component ONCE to preserve component identity across renders
    const BoundaryWrapped = catchExceptionsForComponent(WrappedComponent);

    const ComponentWithBoundary = React.forwardRef<any, P & WithLoadingAndErrorProps>(
        ({ isLoading, error, spinnerSize, ...props }, ref) => {
            if (isLoading) {
                return <CtySpinner size={spinnerSize ?? 'lg'} />;
            }

            if (error) {
                const errorMessage: string =
                    typeof error === 'string'
                        ? error
                        : typeof error?.message === 'string'
                        ? error.message
                        : 'Something went wrong!';
                return (
                    <div className="text-sm py-3 text-center text-danger">
                        {errorMessage}
                    </div>
                );
            }

            return <BoundaryWrapped {...(props as P)} ref={ref} />;
        }
    );

    ComponentWithBoundary.displayName = `ErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return ComponentWithBoundary as React.ComponentType<P & WithLoadingAndErrorProps>;
};

export { ErrorBoundary };